import { Injectable } from '@angular/core';

export enum EPerfTestStep {
  TECH_READINESS = 'tech_readiness',
  TEST_DAY = 'test_day'
}

@Injectable({
  providedIn: 'root'
})
export class StressTestService {

  private videoEl: HTMLVideoElement;
  private instructionalVideoEl: HTMLVideoElement;
  private stressInterval: any;


  private _isTestCompleted: { [step in EPerfTestStep]: boolean } = {
    [EPerfTestStep.TECH_READINESS]: false,
    [EPerfTestStep.TEST_DAY]: false
  };

  public isTestCompleted(step: EPerfTestStep): boolean {
    return this._isTestCompleted[step];
  }

  public setIsTestCompleted(step: EPerfTestStep, value: boolean) {
    this._isTestCompleted[step] = value;
  }

  constructor() { }

  /**
   * Generates a basic device fingerprint based on browser characteristics.
   * In a real prod environment, consider a library like ClientJS or FingerprintJS.
   */
  getDeviceFingerprint(): string {
    const nav = window.navigator;
    const screen = window.screen;

    const components = [
      nav.userAgent,
      nav.language,
      screen.colorDepth,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset(),
      (nav as any).deviceMemory || 'unknown',
      (nav as any).hardwareConcurrency || 'unknown'
    ];

    // Simple hash function for the string
    const str = components.join('###');
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char; // equivalent to (hash * 32) - 31 + char
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash.toString();
  }

  /**
   * Starts playing a video and running JS calculations to mimic
   * WebRTC encoding/decoding and app overhead.
   */
  startStressTest(isDebug: boolean = false) {
    this.startBackgroundVideo(isDebug);
    this.startCpuLoad();
  }

  stopStressTest() {
    if (this.videoEl) {
      this.videoEl.pause();
      this.videoEl.remove();
      this.videoEl = null;
    }
    if (this.stressInterval) {
      clearInterval(this.stressInterval);
    }
  }

  /**
   * Plays a 1080p video sample to stress network download and video decoding.
   * If `isDebug` is true, the video is visible at the bottom right of the page.
   * Otherwise, the video is invisible but still active.
   */
  private startBackgroundVideo(isDebug: boolean = false) {
    // Plays a 1080p nature video sample to stress network download and video decoding
    this.videoEl = document.createElement('video');
    this.videoEl.src = 'https://vea-authoring.vretta.com/user_uploads/4331748/authoring/Big_Buck_Bunny_4K.webm.1080p.vp9/1772145223257/Big_Buck_Bunny_4K.webm.1080p.vp9.webm'; // webm 1080p
    if (isDebug) {
      // DEBUG MODE: Visible at bottom right
      this.videoEl.style.position = 'fixed';
      this.videoEl.style.bottom = '10px';
      this.videoEl.style.right = '10px';
      this.videoEl.style.width = '320px'; // Visible size
      this.videoEl.style.height = 'auto';
      this.videoEl.style.zIndex = '9999';
      this.videoEl.style.border = '2px solid red'; // Visual confirmation
      this.videoEl.style.backgroundColor = 'black';
    } else {
      // PRODUCTION MODE: "Invisible" but active
      this.videoEl.style.position = 'fixed';
      this.videoEl.style.top = '-9999px';
      this.videoEl.style.left = '-9999px';
      this.videoEl.style.width = '1px';
      this.videoEl.style.height = '1px';
      this.videoEl.style.opacity = '0.01';
    }

    this.videoEl.muted = false; // Audio adds to processing load
    this.videoEl.volume = 0.1;  // 0.1 Keep quiet
    this.videoEl.loop = true;
    this.videoEl.setAttribute('playsinline', 'true');

    this.videoEl.onerror = (e) => {
      console.error("Stress Test Video Error:", this.videoEl.error);
    };

    document.body.appendChild(this.videoEl);
    this.videoEl.play().catch(e => console.warn("Auto-play blocked", e));
  }


  /**
   * Plays an instructional video overlay for the given language. This will make sure the candidates can hear and respond.
   * If the element doesn't exist yet, it creates it.
   * If isDebug is true, it shows the video at the bottom right of the screen.
   * If isDebug is false, it hides the video but keeps it playing.
   * @param {string} lang - The language for the instructional video.
   * @param {boolean} [isDebug=false] - Whether to show the video in debug mode.
   */
  startInstructionalVideo(lang: string, isDebug: boolean = false) {
    this.instructionalVideoEl = document.createElement('video');
    this.instructionalVideoEl.id = 'instructional-video-overlay'; // Manually identifying the element

    if (lang == 'en') {
      this.instructionalVideoEl.src = 'https://vea-authoring.vretta.com/user_uploads/4331748/authoring/EN_MPT_TestVideo_V02/1769709713425/EN_MPT_TestVideo_V02.webm';
    }
    if (lang == 'fr') {
      this.instructionalVideoEl.src = 'https://vea-authoring.vretta.com/user_uploads/4331748/authoring/FR_MPT_TestVideo-V02/1769709736223/FR_MPT_TestVideo-V02.webm'
    }
    if (isDebug) {
      // DEBUG MODE: Visible at bottom right
      this.instructionalVideoEl.style.position = 'fixed';
      this.instructionalVideoEl.style.bottom = '10px';
      this.instructionalVideoEl.style.right = '10px';
      this.instructionalVideoEl.style.width = '320px'; // Visible size
      this.instructionalVideoEl.style.height = 'auto';
      this.instructionalVideoEl.style.zIndex = '9999';
      this.instructionalVideoEl.style.border = '2px solid red'; // Visual confirmation
      this.instructionalVideoEl.style.backgroundColor = 'black';
    } else {
      // PRODUCTION MODE: "Invisible" but active
      this.instructionalVideoEl.style.position = 'fixed';
      this.instructionalVideoEl.style.top = '-9999px';
      this.instructionalVideoEl.style.left = '-9999px';
      this.instructionalVideoEl.style.width = '1px';
      this.instructionalVideoEl.style.height = '1px';
      this.instructionalVideoEl.style.opacity = '0.01';
    }

    this.instructionalVideoEl.muted = false;
    this.instructionalVideoEl.volume = 1;
    this.instructionalVideoEl.loop = true;
    this.instructionalVideoEl.setAttribute('playsinline', 'true');

    this.instructionalVideoEl.onerror = (e) => {
      console.error("Stress Test Video Error:", this.instructionalVideoEl.error);
    };

    document.body.appendChild(this.instructionalVideoEl);
    this.instructionalVideoEl.play().catch(e => console.warn("Instruction Video Auto-play blocked", e));

  }

  /**
   * Toggles the instructional video overlay.
   * If the element doesn't exist yet, it creates it.
   */
  public toggleInstructionalVideo(show: boolean, lang: string, isDebug: boolean = false) {
    if (show) {
      if (!this.instructionalVideoEl) {
        this.startInstructionalVideo(lang, isDebug);
      }
      // this.instructionalVideoEl.style.display = 'block';
      // this.instructionalVideoEl.play().catch(e => console.warn("Inst Video Play Error", e));
    } else {
      if (this.instructionalVideoEl) {
        this.instructionalVideoEl.pause();
        this.instructionalVideoEl.style.display = 'none';
        this.instructionalVideoEl.remove();
        this.instructionalVideoEl = null;
      }
    }
  }

  /**
   * Simulates background agent processing by occupying the main thread for 5ms every 100ms.
   * This is intended to mimic the CPU overhead of background agents, and is used to generate load on the system.
   */
  private startCpuLoad() {
    // Run a calculation every 100ms to mimic background agent processing
    this.stressInterval = setInterval(() => {
      const start = performance.now();
      while (performance.now() - start < 5) {
        // Occupy Main Thread for 5ms every 100ms (5% simulated CPU overhead)
        Math.sqrt(Math.random() * Math.random());
      }
    }, 100);
  }
}
